import Presets from '@assets/hdri';
import { Environment, GizmoHelper } from '@react-three/drei';
import { Canvas } from '@react-three/fiber';
import { useAppSelector } from '@store/hooks';
import { Suspense, memo, useEffect } from 'react';
import { DoubleSide, Mesh } from 'three';
import CameraController from './CameraController';
import DrawLines from './DrawLines';
import { CustomGizmoViewcube } from './Gizmo/CustomGizmoViewcube';
import { customFaces } from './Gizmo/gizmoConfig';
import GridWithLabels from './GridWithLabels';
import Measurement from './Measurement/Measurement';
import PoiList from './Poi/PoiList';

const Viewer: React.FC = memo(() => {
  const viewerState = useAppSelector((state) => state.viewer);
  const model = viewerState.project!.model;
  const inspection = viewerState.inspection;
  const roomEnvironment = viewerState.roomEnvironment;

  useEffect(() => {
    model?.traverse((child) => {
      if (child instanceof Mesh) {
        const materials = Array.isArray(child.material)
          ? child.material
          : [child.material];
        materials.forEach((material) => {
          material.side = DoubleSide;
          material.shadowSide = null;
        });
      }
    });
  }, [model]);

  return (
    <Canvas
      id="threejs-canvas"
      style={{ height: '100%', width: '100%', backgroundColor: 'transparent' }}
      gl={{
        antialias: true,
        powerPreference: 'high-performance',
        logarithmicDepthBuffer: true,
        alpha: true,
        stencil: false,
        depth: true,
        failIfMajorPerformanceCaveat: false
      }}
      dpr={Math.min(window.devicePixelRatio, 2)}
      linear
      frameloop="always"
      performance={{ min: 1 }}
    >
      <Suspense fallback={null}>
        {model && (<primitive object={model} />)}
        <PoiList key={inspection?.inspection.info.id} inspection={inspection} />
        <Environment files={Presets[roomEnvironment]} />
      </Suspense>

      <CameraController model={model} />

      <GizmoHelper alignment="bottom-right" margin={[150, 100]}>
        <CustomGizmoViewcube faces={customFaces} opacity={0.9} />
      </GizmoHelper>

      <GridWithLabels model={model} />
      <DrawLines />
      <Measurement />
    </Canvas>
  );
});

export default Viewer;